import i18n from 'i18next'
import { useCallback } from 'react'
import { initReactI18next, useTranslation } from 'react-i18next'
import { config } from '@/app/config'
import { readStorage, writeStorage } from '@/lib/storage'
import { detectLocale, LOCALES, type Locale } from './detect'
import enCommon from './locales/en/common.json'
import esCommon from './locales/es/common.json'
import ptCommon from './locales/pt/common.json'

export { detectLocale, isLocale, LOCALES, type Locale } from './detect'

export const LOCALE_STORAGE_KEY = 'ab.lang'

export const LOCALE_NAMES: Record<Locale, string> = {
  es: 'Español',
  pt: 'Português',
  en: 'English',
}

export const LOCALE_TAGS: Record<Locale, string> = {
  es: 'es-CL',
  pt: 'pt-BR',
  en: 'en-US',
}

function readStoredLocale(): unknown {
  const stored = readStorage<unknown>(LOCALE_STORAGE_KEY, null)
  if (stored !== null) return stored
  try {
    return localStorage.getItem(LOCALE_STORAGE_KEY)
  } catch {
    return null
  }
}

export function initI18n() {
  if (i18n.isInitialized) return
  const locale = detectLocale(readStoredLocale(), config.market)
  void i18n.use(initReactI18next).init({
    resources: { es: { common: esCommon }, pt: { common: ptCommon }, en: { common: enCommon } },
    lng: locale,
    fallbackLng: 'es',
    defaultNS: 'common',
    ns: ['common'],
    interpolation: { escapeValue: false },
    initAsync: false,
  })
  document.documentElement.lang = locale
}

export function registerNamespace(namespace: string, bundles: Record<Locale, object>) {
  for (const locale of LOCALES) {
    i18n.addResourceBundle(locale, namespace, bundles[locale], true, true)
  }
}

export function lazyNamespace(
  namespace: string,
  bundles: Record<Locale, () => Promise<{ default: object }>>,
) {
  let used = false
  const load = async (language: string) => {
    const locale = detectLocale(language, config.market)
    if (i18n.hasResourceBundle(locale, namespace)) return false
    const { default: bundle } = await bundles[locale]()
    i18n.addResourceBundle(locale, namespace, bundle, true, true)
    return true
  }
  i18n.on('languageChanged', (language) => {
    if (!used) return
    void load(language).then((added) => {
      if (added && i18n.language === language) void i18n.changeLanguage(language)
    })
  })
  return () => {
    used = true
    return load(i18n.language)
  }
}

export function useLocale() {
  const { i18n: instance } = useTranslation()
  const locale = detectLocale(instance.language, config.market)
  const setLocale = useCallback(
    (next: Locale) => {
      writeStorage(LOCALE_STORAGE_KEY, next)
      document.documentElement.lang = next
      void instance.changeLanguage(next)
    },
    [instance],
  )
  return { locale, setLocale }
}

export default i18n
